"use client";

import { ArrowRight, Download } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Philosophy() {
  const {
    ref: eyebrowRef,
    isVisible: eyebrowVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>();
  const {
    ref: ledeRef,
    isVisible: ledeVisible
  } = useRevealOnScroll<HTMLParagraphElement>();
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: cardRef,
    isVisible: cardVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const sections = [{
    num: "01",
    label: "INTEGRITY"
  }, {
    num: "02",
    label: "PRECISION"
  }, {
    num: "03",
    label: "STEWARDSHIP"
  }];
  const [activeIndex, setActiveIndex] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? Math.min(scrollTop / docHeight, 1) : 0;
      setScrollProgress(progress);
      const idx = Math.min(sections.length - 1, Math.floor(progress * sections.length));
      setActiveIndex(idx);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="4750" id="our-building-philosophy" className="bg-background text-foreground relative min-h-[90vh] w-full overflow-hidden">
      {}
      <div className="absolute inset-0 z-0">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/moscariello_construction_site.webp" alt="Americo Moscariello overseeing a high-end construction project site" loading="lazy" decoding="async" className="h-full w-full object-cover opacity-50 grayscale" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/70 to-background/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/40" />
      </div>

      {}
      <div className="relative z-10 mx-auto flex min-h-[90vh] max-w-[1400px] flex-col justify-center px-6 py-24 sm:px-10 lg:px-16">
        {}
        <div ref={eyebrowRef} className={`mb-8 flex items-center gap-4 transition-all duration-700 ${eyebrowVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
          <div className="h-px w-12 bg-primary" />
          <span className="font-mono text-xs tracking-widest text-primary uppercase sm:text-sm">
            Philosophy / Moscariello Construction
          </span>
        </div>

        {}
        <h1 ref={headlineRef} className={`font-default font-bold uppercase leading-[0.85] tracking-tight transition-all duration-1000 ${headlineVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`} style={{
        fontSize: "clamp(3.5rem, 13vw, 13rem)"
      }}>
          <span className="block text-foreground">BUILDING</span>
          <span className="block text-transparent" style={{
          WebkitTextStroke: "2px hsl(220 20% 4%)"
        }}>
            LEGACY.
          </span>
        </h1>

        {}
        <p ref={ledeRef} className={`mt-10 max-w-[580px] font-serif text-base leading-relaxed text-muted-foreground transition-all duration-700 sm:text-xl ${ledeVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
          At Moscariello Construction Co, Inc, we blend rugged integrity with refined precision. 
          From foundational structural work to comprehensive property management, we ensure 
          every project in Collegeville stands as a testament to quality and enduring value.
        </p>

        {}
        <div ref={ctaRef} className={`mt-12 flex flex-col gap-4 transition-all duration-700 sm:flex-row sm:items-center ${ctaVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
          <Button asChild size="lg" className="group h-14 bg-primary px-8 font-mono text-sm font-semibold uppercase tracking-widest text-primary-foreground shadow-lg hover:bg-primary/90">
            <Link to="/ConnectWithUsPage">
              Start Your Project
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>

          <Button asChild variant="ghost" size="lg" className="group h-14 px-2 font-mono text-sm font-semibold uppercase tracking-widest text-foreground hover:bg-transparent hover:text-primary">
            <Link to="/TheMoscarielloLegacyPage">
              <Download className="mr-2 h-4 w-4" />
              View Our Legacy
              <span className="ml-2 inline-block h-px w-8 bg-foreground transition-all group-hover:w-12 group-hover:bg-primary" />
            </Link>
          </Button>
        </div>

        {}
        <div ref={cardRef} className={`mt-16 max-w-sm border border-border bg-background/40 p-6 shadow-2xl backdrop-blur-md transition-all duration-1000 sm:absolute sm:bottom-10 sm:left-6 sm:mt-0 lg:left-16 ${cardVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <div className="mb-4 flex items-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-lg bg-primary opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-lg bg-primary" />
            </span>
            <span className="font-mono text-[10px] tracking-widest text-muted-foreground uppercase">
              Active Projects
            </span>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="font-mono text-[10px] tracking-widest text-muted-foreground uppercase">
                Construction
              </div>
              <div className="font-default mt-2 text-4xl font-bold text-primary">08</div>
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-widest text-muted-foreground uppercase">
                Managed
              </div>
              <div className="font-default mt-2 text-4xl font-bold text-foreground">24</div>
            </div>
          </div>
          <div className="mt-4 border-t border-border pt-3">
            <div className="font-mono text-[10px] tracking-widest text-muted-foreground uppercase">
              Serving Collegeville, PA
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="absolute right-4 top-1/2 z-10 hidden -translate-y-1/2 lg:right-8 lg:block">
        <div className="flex flex-col items-end gap-6">
          {sections.map((sec, i) => <div data-index={i} key={sec.num} className={`flex items-center gap-3 transition-all duration-500 ${activeIndex === i ? "opacity-100" : "opacity-40"}`}>
              <span className={`font-mono text-xs tracking-widest uppercase transition-colors ${activeIndex === i ? "text-primary" : "text-muted-foreground"}`}>
                {sec.label}
              </span>
              <span className={`font-mono text-xs font-semibold transition-colors ${activeIndex === i ? "text-primary" : "text-foreground"}`}>
                {sec.num}
              </span>
              <div className={`h-px transition-all duration-500 ${activeIndex === i ? "w-12 bg-primary" : "w-6 bg-border"}`} />
            </div>)}
          <div className="mt-4 h-32 w-px bg-border">
            <div className="w-full bg-primary transition-all duration-150" style={{
            height: `${scrollProgress * 100}%`
          }} />
          </div>
        </div>
      </div>

      {}
      <div className="absolute bottom-4 right-4 z-10 lg:hidden">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs text-primary">{sections[activeIndex].num}</span>
          <span className="font-mono text-xs text-muted-foreground">
            / 0{sections.length}
          </span>
        </div>
      </div>
    </section>;
}